import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useQueryClient } from 'react-query';
import { DepartmentsApi } from '@frontend/api-departments';
import { Page } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useMutation } from '@frontend/react-query-helpers';
import { useAppScopeStore, useOnScopeChange, useScopedQuery } from '@frontend/scope';
import { useSettingsNavigate, useSettingsMatches } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { ConfirmationModal, ContentLoader, Table, TextButton, useModalControl } from '@frontend/design-system';
import { PhoneNumberSettings } from '../components/department/phone-number-settings';
import { PhoneRoutingSettings } from '../components/department/phone-routing-settings';
import { VoiceMailContainer } from '../components/department/voicemail-box-settings';
import { AccordionSection } from '../layouts/accordion';
import { queryKeys } from '../query-keys';
import { logError } from '../utils/loggers';

interface IDepartment {
  id: string;
  showBreadcrumbs?: boolean;
  isNwx?: boolean;
}
export const Department = ({ id, showBreadcrumbs: showBreadcrumb = true }: IDepartment) => {
  const [currSection, setCurrSection] = useState<'phone' | 'routing' | 'voicemail' | undefined>('phone');
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const matches = useSettingsMatches();
  const queryClient = useQueryClient();
  const { selectedLocationIds } = useAppScopeStore();
  const { t } = useTranslation('phone', { keyPrefix: 'departments' });
  const { triggerProps, modalProps } = useModalControl();
  const [departmentId, setDepartmentId] = useState<string>(id);
  const locationId = selectedLocationIds[0];

  useOnScopeChange(() => {
    settingsNavigate({ to: '/phone/departments' });
  });

  const { data: department, isLoading: departmentLoading } = useScopedQuery({
    queryKey: queryKeys.department(id),
    queryFn: async () => {
      return DepartmentsApi.readDepartment({ departmentId }).then((res) => res.department);
    },
    enabled: !!departmentId,
    meta: {
      error: {
        message: 'Department:departmentQuery | Failed to fetch department details',
        data: { departmentId },
      },
    },
  });

  const { mutate: deleteDepartment, isLoading: deleting } = useMutation(
    (id: string) => {
      if (!id) {
        throw new Error(t('No id passed to department DELETE request'));
      }
      return DepartmentsApi.destroy({ departmentId });
    },
    {
      onSuccess: () => {
        // Invalidate so that the department list updates
        queryClient.invalidateQueries([locationId, ...queryKeys.listDepartments()]);
        queryClient.invalidateQueries([locationId, ...queryKeys.listDepartmentsWithSchedules()]);
        queryClient.removeQueries([locationId, ...queryKeys.department(id)]);
        setDepartmentId('');
        modalProps.onClose();
        settingsNavigate({ to: '/phone/departments' });
      },
      onError: (error) => {
        logError('Department:deleteDepartmentMutation | Failed to delete department', {
          data: { departmentId, error },
        });
      },
    }
  );

  const handleDelete = () => {
    deleteDepartment(department?.id ?? '');
  };

  const dynamicBreadcrumbs = useMemo(() => {
    const defaultBreadcrumb = [
      { label: t('Phone') },
      { label: t('Departments'), to: `/portal/phone/departments` },
      { label: department?.name ?? '...' },
    ];
    if (matches[0].pathname.includes('mainline')) {
      defaultBreadcrumb.splice(1, 1);
    }
    return defaultBreadcrumb;
  }, [department?.name, matches[0].pathname]);

  return (
    <Page maxWidth='100%' breadcrumbs={showBreadcrumb ? dynamicBreadcrumbs : undefined} loading={departmentLoading}>
      <ContentLoader show={deleting} message={t('Deleting')} data-testid='department-loader' />

      <ConfirmationModal
        {...modalProps}
        title={t('Confirm this action')}
        message={t('Are you sure you want to delete the {{departmentName}} department?', {
          departmentName: department?.name,
        })}
        destructive
        onConfirm={handleDelete}
      />

      {department ? (
        <div
          css={css`
            section:first-of-type {
              border-top: none;
              padding-top: 0;
            }
          `}
        >
          <AccordionSection
            title={t('Phone Number Settings')}
            description={
              department.mainLine
                ? t(
                    'Enable an extension for internal use and choose the phone numbers associated with your Main Line. Select one voice and one SMS number for the default outbound number.'
                  )
                : t(
                    'Enable an extension for internal use and choose the phone numbers associated with this department. Select one voice and one SMS number for the default outbound number.'
                  )
            }
            isActive={currSection === 'phone'}
            onClick={() => setCurrSection(currSection === 'phone' ? undefined : 'phone')}
            hasTopBorder={true}
          >
            {currSection === 'phone' && <PhoneNumberSettings department={department} />}
          </AccordionSection>
          <AccordionSection
            title={t('Voicemail Box Settings')}
            description={
              <span>
                {t('Name the {{departmentName}} department voicemail box and set the PIN and notifications.', {
                  departmentName: department.name,
                  interpolation: { escapeValue: false },
                })}
                <br />
                {t('The greeting will be set in the call routing section.')}
              </span>
            }
            isActive={currSection === 'voicemail'}
            onClick={() => setCurrSection(currSection === 'voicemail' ? undefined : 'voicemail')}
          >
            {currSection === 'voicemail' && <VoiceMailContainer departmentId={department.id} />}
          </AccordionSection>
          <AccordionSection
            title={t('Phone Hours & Call Routing')}
            description={
              <span>
                {t(
                  'Set when your office is open, closed and if there are any breaks. Decide what should happen when a call comes in—ring the {{departmentName}} devices, go to a phone tree, a call queue, or another routing option. This won’t be displayed to customers.',
                  {
                    departmentName: department.name,
                    interpolation: { escapeValue: false },
                  }
                )}
              </span>
            }
            isActive={currSection === 'routing'}
            onClick={() => setCurrSection(currSection === 'routing' ? undefined : 'routing')}
          >
            {currSection === 'routing' && (
              <PhoneRoutingSettings departmentId={department.id} callRouteId={department.callRouteId} />
            )}
          </AccordionSection>
        </div>
      ) : (
        <Table
          emptyStateConfig={{
            type: 'sync_your_phone',
            header: t('No Main Line'),
            description: t('You have no Main Line set up yet.'),
          }}
          colConfig={[]}
          fullHeight
          hasResponsiveColWidths
          data={[]}
        />
      )}

      {department && !department?.mainLine && (
        <TextButton
          onClick={() => {
            triggerProps.onClick();
          }}
          css={css`
            border: 0;
            color: ${theme.colors.status.critical};
            font-size: 1rem;
            margin: ${theme.spacing(3)} 0;
            padding: 0;
          `}
        >
          {t('Delete Department')}
        </TextButton>
      )}
    </Page>
  );
};
